import { I18n } from '@calendly/i18n';
import { AlertWarningTriangleIcon } from '@calendly/ui/components/icons';
import { HeadingText } from '@calendly/ui/components/typography';
import { colorTextAction, colorTextDestructive, colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { useIsMyCalendlyScope } from 'components/meetings/filter_modal';
import { computeStatusDisplayText } from 'components/meetings/utils';
import { usePackageNameMap } from 'hooks/use_package_name_map';
import { useCurrentTime } from 'shared/contexts';
import { getBadgeKey } from 'shared/utils';
const bodyWithIconCss = "bvjwzeh";
const BodyWithIcon = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyWithIconCss, props.className)} />;
});
const getPossibleBadges = () => ({
  PAYMENT_ERROR: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      <BodyWithIcon>
        <AlertWarningTriangleIcon />
        {I18n.t('publisher.meetings.header_badge.payment_error')}
      </BodyWithIcon>
    </HeadingText>,
  ACTION_NEEDED: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      <BodyWithIcon>
        <AlertWarningTriangleIcon />
        {I18n.t('publisher.meetings.header_badge.action_needed')}
      </BodyWithIcon>
    </HeadingText>,
  EXPIRED: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.expired')}
    </HeadingText>,
  RESCHEDULED: <HeadingText color={colorTextSecondary} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.rescheduled')}
    </HeadingText>,
  CONFLICT: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.conflict')}
    </HeadingText>,
  CANCELLED: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.canceled')}
    </HeadingText>,
  INVITEES_DECLINED: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      <BodyWithIcon>
        <AlertWarningTriangleIcon />
        {I18n.t('publisher.meetings.header_badge.invitees_declined')}
      </BodyWithIcon>
    </HeadingText>,
  NO_SHOW: <HeadingText color={colorTextDestructive} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.no_show')}
    </HeadingText>,
  IN_PROGRESS: <HeadingText color={colorTextAction} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.in_progress')}
    </HeadingText>,
  UP_NEXT: <HeadingText color={colorTextAction} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.up_next')}
    </HeadingText>,
  PAST: <HeadingText color={colorTextSecondary} variant="eyebrow" size="small" as="div">
      {I18n.t('publisher.meetings.header_badge.past_meeting')}
    </HeadingText>
});

/**
 * Renders status badges in the meeting detail right rail header.
 *
 * When StatusIndicator is provided (real-time feature flag on), displays
 * the time-based status. Warning badges are handled separately by MeetingWarningIndicator.
 *
 * When StatusIndicator is not provided, falls back to legacy badges (all badge types).
 *
 * @param {Object} props - Component props
 * @param {Object} props.event - Meeting event object
 * @param {Array} props.todayMeetings - Array of meetings for today. This is used to determine the "up next" and conflict badges.
 * @param {Object} [props.meetingStatus] - Meeting status object with status and startTime (realtime meetings path only)
 * @param {React.ComponentType} [props.StatusIndicator] - StatusIndicator component (when useMeetingsRealtimeFeaturesEnabled)
 */
export const HeaderBadge = ({
  event,
  todayMeetings = [],
  meetingStatus,
  StatusIndicator
}) => {
  const isMyCalendlyScope = useIsMyCalendlyScope();
  const eventUuids = useMemo(() => event?.uuid ? [event.uuid] : [], [event?.uuid]);
  const packageNameMap = usePackageNameMap(eventUuids);
  const isPackageBooking = !!packageNameMap[event?.uuid];
  const currentTime = useCurrentTime();

  // When feature flag is on and status is available, render time-based status (warnings handled separately).
  // If StatusIndicator is provided but meetingStatus is not (or has no status), render null (do not show legacy badge).
  if (StatusIndicator) {
    if (!meetingStatus?.status) return null;
    const displayText = computeStatusDisplayText(meetingStatus.status, meetingStatus.startTime, currentTime);
    if (!displayText) return null;
    return <span data-testid="header-badge">
        <StatusIndicator status={meetingStatus.status} displayText={displayText} />
      </span>;
  }

  // Legacy path: render all badge types
  let toDisplay = getBadgeKey(event, isMyCalendlyScope, {
    otherEventsOnDay: todayMeetings,
    now: currentTime
  });
  if (isPackageBooking && toDisplay === 'PAYMENT_ERROR') toDisplay = undefined;
  const possibleBadges = getPossibleBadges();
  if (!toDisplay || !possibleBadges[toDisplay]) return null;
  return <span data-testid="header-badge-legacy">{possibleBadges[toDisplay]}</span>;
};

require("./header_badge.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./header_badge.js");